import {foldForSearch} from '../utilis/searchMatch';

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const MATRIX_TO_USER_PREFIX = 'https://matrix.to/#/';

export type MatrixMentionTarget =
    | {kind: 'user'; id: string}
    | {kind: 'room'; id: string};

/**
 * A matrix.to link identifies whatever it points at by sigil: `@` a user,
 * `!` a room by id, `#` a room by alias. Mobile writes room mentions with the
 * latter two, so the sigil is what tells a profile link apart from a link that
 * should open a conversation.
 */
export const getMatrixMentionTarget = (href: string): MatrixMentionTarget | null => {
    const trimmedHref = href.trim();
    if (!trimmedHref.startsWith(MATRIX_TO_USER_PREFIX)) {
        return null;
    }

    // Terminates on the query or an event-id suffix, NOT on '#': the matrix.to
    // fragment has already begun, so a further '#' is the room alias sigil and
    // splitting there truncated every alias link to nothing. Same rule as
    // MobileCh3's parseMatrixMentionUrl, so both clients read the wire
    // identically.
    const encodedId = trimmedHref
        .slice(MATRIX_TO_USER_PREFIX.length)
        .split(/[?/]/, 1)[0];
    if (!encodedId) {
        return null;
    }

    let id: string;
    try {
        id = decodeURIComponent(encodedId);
    } catch {
        id = encodedId;
    }

    if (id.startsWith('@')) return {kind: 'user', id};
    if (id.startsWith('!') || id.startsWith('#')) return {kind: 'room', id};

    return null;
};

/**
 * The matrix.to anchor a mention travels as.
 *
 * Mirrors MobileCh3's buildMentionAnchor byte for byte — both clients render
 * each other's messages, so the markup has to agree. The sigil picks the
 * visible prefix: users read "@Name", rooms read "#Name".
 *
 * The label is escaped because names are user-controlled and this markup is
 * emitted into formatted_body verbatim; a group called "</a><img onerror=...>"
 * would otherwise author HTML in every other participant's timeline.
 */
export const buildMentionAnchor = (id: string, label: string): string => {
    const prefix = id.startsWith('!') || id.startsWith('#') ? '#' : '@';
    return `<a href="https://matrix.to/#/${escapeHtml(id)}">${prefix}${escapeHtml(label)}</a>`;
};

export const getMatrixUserIdFromMentionHref = (href: string): string | null => {
    const target = getMatrixMentionTarget(href);
    return target?.kind === 'user' ? target.id : null;
};

const decodeHtmlEntities = (value: string): string => value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

const ANCHOR_PATTERN = /<a\s+href="([^"]*)">([^<]*)<\/a>/g;

export interface ExtractedMentions {
    mentions: Array<{userId: string; displayName: string}>;
    roomMentions: Array<{roomId: string; name: string}>;
}

export const extractMentionsFromFormattedBody = (
    formattedBody: string | undefined,
    mentionedUserIds: string[] | undefined
): ExtractedMentions => {
    const mentions: Array<{userId: string; displayName: string}> = [];
    const roomMentions: Array<{roomId: string; name: string}> = [];
    if (!formattedBody) return {mentions, roomMentions};

    const allowedUserIds = new Set(mentionedUserIds ?? []);
    let match: RegExpExecArray | null;
    ANCHOR_PATTERN.lastIndex = 0;
    while ((match = ANCHOR_PATTERN.exec(formattedBody)) !== null) {
        const [, href, label] = match;
        const target = getMatrixMentionTarget(href);
        if (!target) continue;

        const decodedLabel = decodeHtmlEntities(label);
        if (target.kind === 'user') {
            if (allowedUserIds.size > 0 && !allowedUserIds.has(target.id)) continue;
            mentions.push({userId: target.id, displayName: decodedLabel.replace(/^@/, '')});
        } else {
            roomMentions.push({roomId: target.id, name: decodedLabel.replace(/^#/, '')});
        }
    }

    return {mentions, roomMentions};
};

export interface CaretTextPosition {
    node: Text;
    offset: number;
}

/**
 * Resolves a caret to the text node it sits in, so the characters before it can
 * be read as a mention query.
 *
 * A range anchored in a text node counts its offset in characters, but a range
 * anchored in an element counts it in child nodes — and a caret placed with
 * `setStartAfter`, the way emoji insertion places it, always takes the element
 * form. Slicing that node index off the element's `textContent` yields an
 * arbitrary prefix of the whole composer, which can end in a stale `@` and
 * reopen the mention list over content the user never typed.
 */
export const resolveCaretTextPosition = (container: Node, offset: number): CaretTextPosition | null => {
    if (container.nodeType === Node.TEXT_NODE) {
        return {node: container as Text, offset};
    }

    const nodeBeforeCaret = container.childNodes[offset - 1];
    if (nodeBeforeCaret?.nodeType !== Node.TEXT_NODE) {
        return null;
    }

    const textNode = nodeBeforeCaret as Text;
    return {node: textNode, offset: textNode.textContent?.length ?? 0};
};

const MENTION_QUERY_PATTERN = /@([^@]*)$/;

/**
 * The room-link query being typed after "#".
 *
 * Unlike "@", "#" turns up in ordinary writing — "issue #42", "#1 priority",
 * "#fff" — so it only opens a query at the start of the text or after
 * whitespace, never mid-word. That still lets "issue #42" through; what stops
 * a list appearing there is that no room matches "42", which is the same guard
 * mobile relies on.
 */
const ROOM_MENTION_QUERY_PATTERN = /(?:^|\s)#([^#\s][^#]*|)$/;

export const getRoomMentionQueryMatch = (position: CaretTextPosition | null): RegExpMatchArray | null => {
    if (!position) return null;

    const textBeforeCaret = position.node.textContent?.slice(0, position.offset) ?? '';
    return textBeforeCaret.match(ROOM_MENTION_QUERY_PATTERN);
};

export const getMentionQueryMatch = (position: CaretTextPosition | null): RegExpMatchArray | null => {
    if (!position) return null;

    const textBeforeCaret = position.node.textContent?.slice(0, position.offset) ?? '';
    return textBeforeCaret.match(MENTION_QUERY_PATTERN);
};

const MENTION_PILL_STYLE = 'background-color: #e3f2fd; color: #1565c0; padding: 2px 4px; border-radius: 4px; font-weight: 500;';

export const hydrateMentionPills = (
    escapedText: string,
    mentions: Array<{userId: string; displayName: string}>
): string => {
    if (mentions.length === 0) return escapedText;

    const sorted = [...mentions].sort((a, b) => escapeHtml(b.displayName).length - escapeHtml(a.displayName).length);
    const pattern = new RegExp(
        `@(?:${sorted.map(m => escapeRegExp(escapeHtml(m.displayName))).join('|')})(?![\\p{L}\\p{N}])`,
        'gu'
    );

    return escapedText.replace(pattern, match => {
        const displayName = match.slice(1);
        const user = sorted.find(m => escapeHtml(m.displayName) === displayName);
        return user
            ? `<span class="mention" data-user-id="${escapeHtml(user.userId)}" style="${MENTION_PILL_STYLE}">${match}</span>`
            : match;
    });
};

export const hydrateRoomMentionPills = (
    escapedText: string,
    roomMentions: Array<{roomId: string; name: string}>
): string => {
    if (roomMentions.length === 0) return escapedText;

    const sorted = [...roomMentions].sort((a, b) => escapeHtml(b.name).length - escapeHtml(a.name).length);
    const pattern = new RegExp(
        `#(?:${sorted.map(r => escapeRegExp(escapeHtml(r.name))).join('|')})(?![\\p{L}\\p{N}])`,
        'gu'
    );

    return escapedText.replace(pattern, match => {
        const name = match.slice(1);
        const room = sorted.find(r => escapeHtml(r.name) === name);
        return room
            ? `<span class="mention" data-room-id="${escapeHtml(room.roomId)}" style="${MENTION_PILL_STYLE}">${match}</span>`
            : match;
    });
};

export const formatMediaCaptionWithMentions = (
    caption: string,
    mentions: Array<{userId: string; displayName: string}>
): {formattedBody: string; userIds: string[]} | null => {
    let formattedBody = escapeHtml(caption.trim());
    const userIds: string[] = [];

    mentions.forEach(mention => {
        const safeMentionText = escapeHtml(`@${mention.displayName}`);
        const mentionPattern = new RegExp(escapeRegExp(safeMentionText), 'gi');
        const nextFormattedBody = formattedBody.replace(
            mentionPattern,
            `<a href="https://matrix.to/#/${escapeHtml(mention.userId)}">${safeMentionText}</a>`
        );

        if (nextFormattedBody !== formattedBody && !userIds.includes(mention.userId)) {
            userIds.push(mention.userId);
        }
        formattedBody = nextFormattedBody;
    });

    return userIds.length > 0 ? {formattedBody, userIds} : null;
};

const toFoldedWords = (value: string): string[] =>
    foldForSearch(value).folded.split(/\s+/).filter((word) => word.length > 0);

export const matchesMentionQuery = (displayName: string, query: string): boolean => {
    const queryWords = toFoldedWords(query);
    if (!queryWords.length) {
        return true;
    }
    const nameWords = toFoldedWords(displayName);
    return queryWords.every((queryWord) => nameWords.some((nameWord) => nameWord.startsWith(queryWord)));
};
